import React, { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useDebounce } from "@/hooks/use-debounce";
import { StocksRawFiltersProps } from "./types";
import { supabase } from "@/integrations/supabase/client";

export function StocksRawFilters({
  searchText,
  onSearchChange,
  selectedType,
  selectedExchange,
  onTypeFilterChange,
  onExchangeFilterChange,
  totalCount,
  visibleCount,
}: StocksRawFiltersProps) {
  const [exchanges, setExchanges] = useState<{value: string, label: string}[]>([]);
  const [equityTypes, setEquityTypes] = useState<{value: string, label: string}[]>([]);
  const [localSearchText, setLocalSearchText] = useState(searchText);
  
  // Use debounced search to reduce API calls
  const debouncedSearchText = useDebounce(localSearchText, 300);
  
  // Sync debounced search text with parent component
  useEffect(() => {
    onSearchChange(debouncedSearchText);
  }, [debouncedSearchText, onSearchChange]);
  
  // Fetch exchanges and types from database
  // Set default filter options while database loads
  useEffect(() => {
    // Set exact equity types as specified
    setEquityTypes([
      { value: 'ETF', label: 'ETF' },
      { value: 'Common Stock', label: 'Common Stock' },
      { value: 'Preferred Stock', label: 'Preferred Stock' }
    ]);

    // Set exact exchanges as specified - must match database 'exchange_name' values
    setExchanges([
      { value: 'Toronto Stock Exchange', label: 'Toronto Stock Exchange' },
      { value: 'TSX Venture Exchange', label: 'TSX Venture Exchange' }
    ]);
    
    // Using full exchange names that match exactly what's in the exchange_name column

    // We're using hardcoded options as specified
    // No need to fetch from database
  }, []);
  // Clear all filters
  const handleClearFilters = () => {
    setLocalSearchText("");
    onSearchChange("");
    onTypeFilterChange(null);
    onExchangeFilterChange(null);
  };

  // Calculate active filter count for badge
  const activeFilterCount = [
    (debouncedSearchText?.trim()?.length || 0) > 0,
    !!selectedType,
    !!selectedExchange
  ].filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Type Filter (ETF, Common Stock, etc) */}
        <Select
          value={selectedType || "all"}
          onValueChange={(value) => 
            onTypeFilterChange(value === "all" ? null : value)
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Equity Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Equity Types</SelectLabel>
              <SelectItem value="all">All Equities</SelectItem>
              {equityTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        
        {/* Exchange Filter */}
        <Select
          value={selectedExchange || "all"}
          onValueChange={(value) => 
            onExchangeFilterChange(value === "all" ? null : value)
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Exchange" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Exchanges</SelectLabel>
              <SelectItem value="all">All Exchanges</SelectItem>
              {exchanges.map((exchange) => (
                <SelectItem key={exchange.value} value={exchange.value}>
                  {exchange.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by ticker symbol or name..."
            value={localSearchText}
            onChange={(e) => setLocalSearchText(e.target.value)}
            className="pl-9 w-full"
          />
          {localSearchText && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1 h-7 w-7 p-0"
              onClick={() => setLocalSearchText("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Clear filters button */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="flex items-center gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Clear Filters
            {activeFilterCount > 1 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      
      {/* Filter summary */}
      <div className="text-xs text-muted-foreground">
        Showing <span className="font-medium">{visibleCount.toLocaleString()}</span> of <span className="font-medium">{totalCount.toLocaleString()}</span> stocks
      </div>
    </div>
  );
}

export default StocksRawFilters;
